import { ErrorModal, useUTLSettingsContext } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { useEffect } from "react";
import { LazyLoading } from "../../../controls/LazyLoading";
import { NotificationSnackBar } from "../../../controls/snackbars/notification-snackbar";
import { ChemistrySample } from "../models/ElementData";
import { StrandTimeSeriesData } from "../models/StrandSpeed";
import {
	CalculateProcessData,
	GetProcessCatalogs,
	getBilletTempByProcessId,
	getChemistrySampleByHeatId,
	getMoldLevelsByProcessId,
	getProcessDataById,
	getProcessesWithoutDeliveryTicket,
	getStrandSpeedsByProcessId,
} from "../repositories/ProductionRepository";
import { useCastersContext } from "./CastersContext";
import { DataEntryPageV2 } from "./components/DataEntryPageV2";
import { useDeliveryTicketAlertAccess } from "./hooks/useDeliveryTicketAlertAccess";
import { useTimer } from "./hooks/useTimer";

export const CastersPage = () => {
	const { state, actions } = useCastersContext();
	const { state: utlSettingsState } = useUTLSettingsContext();

	const CAS_BILLET_TEMP_OFFSET_MINUTES = Number(
		get(
			utlSettingsState.settings,
			"CAS_DE_BILLET_TEMP_CHART_OFFSET_MINUTES",
			20,
		),
	);

	const { hasAccess, CASTER_DE_DELIVERY_TICKET_ALERT_OFFSET_TIME_IN_MINUTES } =
		useDeliveryTicketAlertAccess();

	const getDataFromAPI = async (forceProbe: boolean = false) => {
		if (state.processId === false) return;
		actions.setIsLoadingBackground(true);
		const response = await getProcessDataById(state.processId);
		actions.setIsLoadingBackground(false);
		if (response.ok) {
			const processData = get(response, "data.tables[0].rows[0]", {});
			const strands = get(response, "data.tables[1].rows", []);
			const probes = get(response, "data.tables[2].rows", []);
			const lostStrands = get(response, "data.tables[3].rows", []);
			const scraps = get(response, "data.tables[4].rows", []);
			const process = {
				processData,
				strands,
				probes,
				lostStrands,
				scraps,
				elements: [] as ChemistrySample[], // response.data.tables[3].rows[0],
				strandSpeeds: [] as StrandTimeSeriesData[],
				moldLevels: [] as StrandTimeSeriesData[],
				billetTemp: [] as StrandTimeSeriesData[],
			};
			actions.setIsLoadingBackground(true);
			const elementResponse = await getChemistrySampleByHeatId(
				process.processData.HeatId,
			);
			const strandSpeedsResponse = await getStrandSpeedsByProcessId(
				process.processData.ProcessId,
			);
			const moldLevelsResponse = await getMoldLevelsByProcessId(
				process.processData.ProcessId,
			);
			const billetTempResponse = await getBilletTempByProcessId(
				process.processData.ProcessId,
				CAS_BILLET_TEMP_OFFSET_MINUTES,
			);
			actions.setIsLoadingBackground(false);
			if (elementResponse.ok) {
				process.elements = elementResponse.data.tables[0].rows;
			} else {
				actions.setErrorMessage(elementResponse.message);
			}
			if (strandSpeedsResponse.ok) {
				process.strandSpeeds = strandSpeedsResponse.data.tables[0].rows;
			} else {
				actions.setErrorMessage(strandSpeedsResponse.message);
			}
			if (moldLevelsResponse.ok) {
				process.moldLevels = moldLevelsResponse.data.tables[0].rows;
			} else {
				actions.setErrorMessage(moldLevelsResponse.message);
			}
			if (billetTempResponse.ok) {
				process.billetTemp = billetTempResponse.data.tables[0].rows;
			} else {
				actions.setErrorMessage(billetTempResponse.message);
			}
			if (state.processId !== process.processData.ProcessId) {
				actions.setProcess(process);
				actions.setProcessId(process.processData.ProcessId);
			} else {
				actions.mergeProcess(process);
			}
		} else {
			actions.setErrorMessage(response.message);
		}

		// Fetch processes without delivery ticket if user has access
		if (hasAccess) {
			const resp = await getProcessesWithoutDeliveryTicket(
				CASTER_DE_DELIVERY_TICKET_ALERT_OFFSET_TIME_IN_MINUTES,
			);
			if (resp.ok) {
				const processes = get(resp, "data.tables[0].rows", []);
				actions.setProcessesWithoutDeliveryTicket(processes);
			}
		}
	};
	const getCatalogDataFromAPI = async () => {
		actions.setIsLoadingBackground(true);
		const response = await GetProcessCatalogs();
		if (response.ok) {
			actions.setCatalogs({
				tundishes: response.data.tables[0].rows,
				pourBacks: response.data.tables[1].rows,
				moldSizes: response.data.tables[2].rows,
				tundishShrouds: response.data.tables[3].rows,
				scrapReasons: response.data.tables[4].rows,
				tonnageReasons: response.data.tables[5].rows,
				housings: response.data.tables[6].rows,
				ladles: response.data.tables[7].rows,
				grades: response.data.tables[8].rows,
			});
		} else {
			actions.setErrorMessage(response.message);
		}
		actions.setIsLoadingBackground(false);
	};
	const getProcessData = async (processId: number | null) => {
		if (processId !== state.processId) actions.setProcessId(processId);
	};
	const calculateProcessData = async () => {
		if (state.processData === null) return;
		actions.clearFocusField();
		actions.setIsLoadingBackground(true);
		const resp = await CalculateProcessData(state.processData.ProcessId ?? 0);
		actions.setIsLoadingBackground(false);
		if (resp.ok) {
			actions.setNotificationMessage(
				"Process data was calculated successfully",
			);
			await getDataFromAPI();
		} else {
			actions.setIsLoadingBackground(false);
			actions.setErrorMessage(resp.message);
		}
	};
	/////
	const updateHistory = (processId: string) => {
		const paths = location.pathname.split("/");
		window.history.replaceState(null, "", `/${paths[1]}` + `/${processId}`);
	};

	useEffect(() => {
		if (
			state.processData?.ProcessId !== null &&
			state.processData?.ProcessId === state.processId
		)
			return;
		(async () => {
			await getDataFromAPI();
		})();
		updateHistory(state.processId ? state.processId?.toString() : "");
	}, [state.processId]);

	useEffect(() => {
		(async () => {
			await getCatalogDataFromAPI();
		})();
	}, []);

	const { restartTime } = useTimer({
		onTimeUp: getDataFromAPI,
	});

	if (state.isLoadingPage) return <LazyLoading />;

	return (
		<>
			<button
				id="dummyDiv"
				style={{
					width: "0px",
					height: "0px",
					opacity: "0",
					position: "absolute",
				}}
			/>
			{state.processData !== null && (
				<DataEntryPageV2
					reloadProcess={getProcessData}
					calculateProcess={calculateProcessData}
					refreshData={async () => {
						await getDataFromAPI();
						restartTime();
					}}
				/>
			)}
			<ErrorModal
				error={state.errorMessage}
				onHide={() => actions.setErrorMessage("")}
			/>
			<NotificationSnackBar
				message={state.notificationMessage}
				onHide={() => actions.setNotificationMessage("")}
			/>
		</>
	);
};
